import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApplicationIdContext } from '../ApplicationContext';
import { ApplicationSignStatusModel } from '../../serverGeneratedTypings/applicationSignStatusModel';
import { ApplicationStatusModel } from '../../serverGeneratedTypings/applicationStatusModel';
import { N2ApplicationStatus } from '../../serverGeneratedTypings/n2ApplicationStatus';
import { getApplicationSignStatus } from '@n2-common/services/legacyApplicationApiService';
import { logInfoToServerSide } from '../../services/serverSideLogging';
import { fetchApplicationStatusAndRedirectIfNotExpectedState } from '../../applicationPages/applicationStatusHelpers';
import { CommonStatusPageProps } from '../ApplicationStatusPage';

export interface ApplicationSignContextProps {
    applicationId: string;
    applicantId: string;
    isMainApplicant: boolean | null;
    applicationSignStatus: ApplicationSignStatusModel | null;
    applicationStatus: ApplicationStatusModel | null;
}

export function applicationIsInValidStateForSigning(applicationStatus: ApplicationStatusModel) {
    if (applicationStatus.Status !== N2ApplicationStatus.ReadyForSigning) {
        return false;
    }
    if (applicationStatus.IsMainApplicant) {
        return !applicationStatus.ApplicantCddPending;
    } else {
        return !applicationStatus.CosignerCddPending;
    }
}

export const ApplicationSignContext = createContext<ApplicationSignContextProps>({
    applicantId: '',
    applicationId: '',
    isMainApplicant: null,
    applicationSignStatus: null,
    applicationStatus: null,
});

export const useApplicationSignContext = () => {
    const res = useContext(ApplicationSignContext);
    if (!res) {
        throw new Error('ApplicationSignContext is not accessible if not used inside SignCommonComponent (ApplicationSignContextProvider).');
    }
    return res;
};

type SignCommonComponentProps = /*InsuranceProps &*/ CommonStatusPageProps & React.PropsWithChildren;

export function SignCommonComponent({ children, navigateToCorrectStepFunc }: SignCommonComponentProps): JSX.Element {
    const navigate = useNavigate();
    const { applicationId, applicantId } = useApplicationIdContext();

    const [isMainApplicant, setIsMainApplicant] = useState<boolean | null>(null);

    const [applicationSignStatus, setApplicationSignStatus] = useState<ApplicationSignStatusModel | null>(null);

    const [applicationStatus, setApplicationStatus] = useState<ApplicationStatusModel | null>(null);

    useEffect(() => {
        fetchApplicationStatusAndRedirectIfNotExpectedState(
            applicationId,
            applicantId,
            applicationIsInValidStateForSigning,
            setApplicationStatus,
            navigate,
            navigateToCorrectStepFunc
        );
    }, [applicationId, navigate, applicantId, navigateToCorrectStepFunc]);

    useEffect(() => {
        if (!applicationStatus) {
            return;
        }
        const mainApp = applicationStatus.IsMainApplicant;
        logInfoToServerSide(`Showing sign page for ${mainApp ? 'mainApplicant' : 'coApplicant'} `, applicationId, applicantId);
        setIsMainApplicant(mainApp);
    }, [applicationStatus, applicantId, applicationId]);


    useEffect(() => {
        const controller = new AbortController();
        if (!applicationId) {
            return;
        }
        getApplicationSignStatus(applicationId, controller.signal).then((res) => {
            setApplicationSignStatus(res);
        });
        return () => controller.abort();
    }, [applicationId]);

    return (
        <ApplicationSignContext.Provider
            value={{
                applicationId,
                applicantId,
                isMainApplicant,
                applicationSignStatus,
                applicationStatus
            }}
        >
            {children}
        </ApplicationSignContext.Provider>
    );
}
